import { useEffect, useRef, useState } from "react";

const JODIT_BUTTONS = [
  "brush",
  "bold",
  "underline",
  "eraser",
  "|",
  "font",
  "fontsize",
  "paragraph",
  "|",
  "ul",
  "ol",
  "|",
  "align",
  "|",
  "table",
  "link",
  "image",
  "video",
  "|",
  "fullsize",
  "source",
  "help",
];

const buildConfig = ({ placeholder, minHeight }) => ({
  height: Math.max(minHeight + 80, 280),
  minHeight,
  toolbarAdaptive: false,
  toolbarSticky: false,
  statusbar: false,
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  placeholder: placeholder || "Write chapter content…",
  buttons: JODIT_BUTTONS,
  buttonsMD: JODIT_BUTTONS,
  buttonsSM: JODIT_BUTTONS,
  buttonsXS: JODIT_BUTTONS,
  defaultActionOnPaste: "insert_clear_html",
  askBeforePasteHTML: false,
  askBeforePasteFromWord: false,
  style: {
    font: "'Barlow', system-ui, sans-serif",
    fontSize: "14px",
    color: "#3D2914",
  },
  controls: {
    font: {
      list: {
        "'Barlow',system-ui,sans-serif": "Barlow",
        "Arial,Helvetica,sans-serif": "Arial",
        "Georgia,serif": "Georgia",
        "'Times New Roman',Times,serif": "Times New Roman",
      },
    },
  },
  link: {
    openInNewTabCheckbox: true,
  },
  image: {
    openOnDblClick: true,
  },
});

const getJodit = () => window.Jodit;

export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Write chapter content…",
  invalid = false,
  minHeight = 220,
}) {
  const hostRef = useRef(null);
  const editorRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const lastValue = useRef(value || "");
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  onChangeRef.current = onChange;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 120;

    const init = () => {
      if (cancelled || !hostRef.current || editorRef.current) return;

      const Jodit = getJodit();
      if (!Jodit?.make) {
        attempts += 1;
        if (attempts >= maxAttempts) {
          setFailed(true);
          return;
        }
        window.setTimeout(init, 50);
        return;
      }

      try {
        const editor = Jodit.make(hostRef.current, {
          ...buildConfig({ placeholder, minHeight }),
          value: value || "",
        });

        editorRef.current = editor;
        lastValue.current = value || "";
        setReady(true);

        const emit = () => {
          const html = editor.value;
          lastValue.current = html;
          onChangeRef.current?.(html);
        };

        editor.events.on("change", emit);
        editor.events.on("blur", emit);
      } catch (err) {
        console.error("Jodit init failed", err);
        setFailed(true);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (editorRef.current) {
        editorRef.current.destruct();
        editorRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const next = value || "";
    if (next !== lastValue.current && editor.value !== next) {
      editor.value = next;
      lastValue.current = next;
    }
  }, [value]);

  if (failed) {
    return (
      <textarea
        className={`form-control${invalid ? " is-invalid" : ""}`}
        rows={8}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  }

  return (
    <div
      className={`rich-text-editor jodit-host${ready ? " is-ready" : ""}${invalid ? " is-invalid" : ""}`}
    >
      <textarea
        ref={hostRef}
        defaultValue={value || ""}
        className="rte-fallback"
        rows={8}
        readOnly={ready}
        placeholder={ready ? undefined : "Loading editor…"}
      />
    </div>
  );
}
